import { FC, useCallback, useEffect, useMemo } from 'react';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@packages/utils';
import { Table } from 'components/Base/Table';
import { EWaitListColumnKey, TWaitListUI } from 'data/types/registrants.types';
import {
  useGetWaitListQuery,
  useExportWaitlistMutation,
} from 'redux/apiSlice/registrants.slice';
import { EIconNames } from 'components/Base/Icon';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { workshopsApi } from 'redux/apiSlice/workshops.slice';
import { TabBodyWrapper } from 'components/Base/Tabs';
import { EWorkshopStatus } from 'data/types/workshops.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { Flex } from 'antd';
import { useIsRfkAdmin } from 'hooks/useIsRfkAdmin';
import { TWorkshopWaitListProps } from './WorkshopWaitList.types';
import useGetData from './WorkshopWaitList.data';
import MoveAction from './Actions/MoveAction';
import DeleteAction from './Actions/DeleteAction';
import { EUserType } from '../../../../../../../data/types/user.types';
import { useAppSelector } from '../../../../../../../redux/store';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EWaitListColumnKey.CREATION_DATE]: 'registrationDate',
  [EWaitListColumnKey.FIRST_NAME]: 'firstName',
  [EWaitListColumnKey.LAST_NAME]: 'lastName',
  [EWaitListColumnKey.AGE_GROUP]: 'ageGroup',
  countOfChildren: 'countOfChildren',
  childrenNames: 'childrenNames',
  phone: 'phone',
  email: 'email',
  customQuestionAnswer: 'questionAnswer',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EWaitListColumnKey.CREATION_DATE]: 'CREATION_DATE',
  [EWaitListColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EWaitListColumnKey.LAST_NAME]: 'LAST_NAME',
};

const ADMIN_TYPES = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
];

const WorkshopWaitList: FC<TWorkshopWaitListProps> = ({
  workshopId,
  customQuestion,
  hideChildInfo,
}) => {
  const usersData = useAppSelector(state => state.userSlice);

  const isAdmin = useMemo(() => {
    const user = usersData?.user;
    return user?.type?.some((type: EUserType) => ADMIN_TYPES.includes(type));
  }, [usersData?.user]);

  const isRfkAdmin = useIsRfkAdmin();

  const { defaultColumns, defaultSortParam } = useGetData();
  const params = useParams();
  const { id } = params as { id: string };
  const { t: tEnums } = useTranslation({
    keyPrefix: 'enums',
  });

  const { data: workshopDetailsCachedData } =
    workshopsApi.endpoints.getWorkshopById.useQueryState(
      {
        id,
      },
      { skip: !id },
    );
  const workshopPossibleAgeGroups =
    workshopDetailsCachedData?.possibleAgeGroups;

  const storageKey = `${ETableStoragePaths.WORKSHOP_WAIT_LIST}-${workshopId}`;

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TWaitListUI>({
    storagePath: storageKey,
    defaultColumns,
    defaultSortColumn: defaultSortParam,
  });
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.workshops.details.workshop-wait-list',
  });

  const { data, isFetching, isLoading } = useGetWaitListQuery({
    page: 0,
    pageSize: 2000,
    sortColumn,
    filters: selectedFilters,
    workshopId,
  });

  const isReadonly =
    workshopDetailsCachedData?.status !== EWorkshopStatus.PUBLISHED ||
    (workshopDetailsCachedData?.status === EWorkshopStatus.PUBLISHED &&
      data?.allowedOperations.readonly);

  useEffect(() => {
    if (!data) {
      return;
    }
    let cols = defaultColumns.map(item => {
      if (!isReadonly && item.dataIndex === 'actions') {
        return {
          ...item,
          width: isRfkAdmin ? 90 : 50,
          render: (_: unknown, record: TWaitListUI) => (
            <Flex gap={4} align="center">
              <MoveAction
                workshopClassId={record.workshopClassId}
                registrantId={record.registrantId}
                workshopId={workshopId}
              />
              {isRfkAdmin && (
                <DeleteAction
                  registrantId={record.registrantId}
                  workshopId={workshopId}
                />
              )}
            </Flex>
          ),
        };
      }
      if (
        item.key === EWaitListColumnKey.AGE_GROUP &&
        workshopPossibleAgeGroups
      ) {
        return {
          ...item,
          filters: workshopPossibleAgeGroups.map(ageGroup => ({
            text: tEnums(`age-group.${ageGroup}`),
            value: ageGroup,
          })),
        };
      }
      return item;
    });
    const hasQuestionAnswers = data?.content?.some(r => r.customQuestionAnswer);
    const showQuestionColumn = !!customQuestion || !!hasQuestionAnswers;

    if (!showQuestionColumn || hideChildInfo) {
      cols = cols.filter(col => {
        if (!showQuestionColumn && col.dataIndex === 'customQuestionAnswer') {
          return false;
        }
        return !(
          hideChildInfo &&
          (col.dataIndex === 'countOfChildren' ||
            col.dataIndex === 'childrenNames')
        );
      });
    }
    setColumns(cols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    customQuestion,
    hideChildInfo,
    isReadonly,
    isRfkAdmin,
    workshopPossibleAgeGroups,
  ]);

  const [exportWaitlist, { isLoading: isExporting }] =
    useExportWaitlistMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, [storageKey]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        workshopId,
      };
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      if (selectedFilters?.[EWaitListColumnKey.AGE_GROUP]) {
        filters.ageGroups = selectedFilters[EWaitListColumnKey.AGE_GROUP];
      }
      return filters;
    },
    [workshopId, selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportWaitlist({
        columns: exportColumns,
        filters: buildExportFilters(),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportWaitlist,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportWaitlist({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportWaitlist,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <TabBodyWrapper
      isEmpty={!data?.content.length && !hasSelectedFilter && !isFetching}
      isLoading={isLoading}
      emptyText={t('empty-view.TITLE')}
      emptyIcon={EIconNames.SAND_TIMER_FILLED}
    >
      <Table<TWaitListUI>
        tableTitle={t('TITLE')}
        tabHeight={44}
        onFiltersReset={onFiltersReset}
        hasFilters={hasFilters}
        currentPage={page}
        columns={columns}
        additionalActions={[exportAction]}
        dataSource={data?.content}
        loading={isFetching}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalDataCount={data?.totalElements}
        onChange={handleTableChange}
        rowKey={record => record.registrantId}
        rowClassName={!isAdmin ? 'pe-none' : ''}
        storagePath={storageKey}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    </TabBodyWrapper>
  );
};

export default WorkshopWaitList;
